import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import { prefersReducedMotion, random } from './useCanvasAnimation';

const TURKEY_WIDTH = 76;
const TURKEY_HEIGHT = 68;
const WALK_SPEED = 0.09; // px per ms - about 15s to cross a laptop screen
const PAUSE_BETWEEN_WALKS = 6_000;
const FIRST_WALK_DELAY = 3_000;
const GOBBLE_PAUSE = 1_000;
const FEATHER_BURST_LENGTH = 1_400;
const FEATHERS_PER_BURST = 14;

const FEATHER_COLORS = ['#9c3b22', '#d9822b', '#e8b04a', '#8c5a2b'];
const TAIL_ANGLES = [-62, -40, -20, 0, 20, 40, 62];

type Feather = { id: number; dx: number; dy: number; rotate: number; color: string; delay: number };

const bob = keyframes`
  to { transform: translateY(-2px) rotate(-2deg); }
`;
const step = keyframes`
  from { transform: rotate(-18deg); }
  to { transform: rotate(18deg); }
`;
const gobblePop = keyframes`
  0% { transform: scale(0.4); opacity: 0; }
  20% { transform: scale(1.1); opacity: 1; }
  80% { opacity: 1; }
  100% { transform: translateY(-10px); opacity: 0; }
`;
// thrown up and out, then floats down while spinning, like confetti
const featherFly = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
  35% { transform: translate(var(--dx), var(--dy)) rotate(calc(var(--rotate) * 0.4)); opacity: 1; }
  100% { transform: translate(calc(var(--dx) * 1.4), calc(var(--dy) + 90px)) rotate(var(--rotate)); opacity: 0; }
`;

const makeFeathers = (): Feather[] =>
  Array.from({ length: FEATHERS_PER_BURST }, (_, i) => {
    const angle = random(-Math.PI * 0.92, -Math.PI * 0.08);
    const distance = random(50, 120);
    return {
      id: i,
      dx: Math.cos(angle) * distance,
      dy: Math.sin(angle) * distance,
      rotate: random(-540, 540),
      color: FEATHER_COLORS[i % FEATHER_COLORS.length],
      delay: random(0, 80),
    };
  });

/**
 * Thanksgiving: a turkey walks back and forth along the bottom of the screen, resting off screen between passes.
 * Clicking it makes it stop, gobble and throw feathers. Position is written straight to the DOM (see GhostAnimation).
 */
export const TurkeyTrot = () => {
  const ref = useRef<HTMLDivElement>(null);
  const pausedUntil = useRef(0);
  const hasWalked = useRef(false);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [isWalking, setIsWalking] = useState(false);
  const [gobbleKey, setGobbleKey] = useState(0);
  const [isGobbling, setIsGobbling] = useState(false);
  const [feathers, setFeathers] = useState<Feather[]>([]);
  const gobbleTimeouts = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => () => gobbleTimeouts.current.forEach(clearTimeout), []);

  // between passes: wait, then start the next walk (the walk effect flips direction when it exits)
  useEffect(() => {
    if (isWalking || prefersReducedMotion()) return undefined;
    const timeout = setTimeout(() => setIsWalking(true), hasWalked.current ? PAUSE_BETWEEN_WALKS : FIRST_WALK_DELAY);
    return () => clearTimeout(timeout);
  }, [isWalking]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !isWalking) return undefined;

    let x = direction === 1 ? -TURKEY_WIDTH : window.innerWidth;
    let last = performance.now();
    let frame = 0;

    const animate = (now: number) => {
      const dt = Math.min(now - last, 50);
      last = now;
      if (now >= pausedUntil.current) x += direction * WALK_SPEED * dt;
      el.style.transform = `translate3d(${x}px, 0, 0)`;

      const isOffScreen = direction === 1 ? x > window.innerWidth : x < -TURKEY_WIDTH;
      if (isOffScreen) {
        hasWalked.current = true;
        setDirection((d) => (d === 1 ? -1 : 1));
        setIsWalking(false);
        return;
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(frame);
  }, [direction, isWalking]);

  const handleGobble = () => {
    pausedUntil.current = performance.now() + GOBBLE_PAUSE;
    setGobbleKey((key) => key + 1);
    setIsGobbling(true);
    setFeathers(makeFeathers());
    gobbleTimeouts.current.forEach(clearTimeout);
    gobbleTimeouts.current = [
      setTimeout(() => setIsGobbling(false), GOBBLE_PAUSE),
      setTimeout(() => setFeathers([]), FEATHER_BURST_LENGTH + 100),
    ];
  };

  return (
    <Box ref={ref} sx={[sxStyles.container, { display: isWalking ? 'block' : 'none' }]}>
      {isGobbling && (
        <Box key={gobbleKey} sx={sxStyles.bubble}>
          Gobble!
        </Box>
      )}
      {feathers.map((feather) => (
        <Box
          key={`${gobbleKey}-${feather.id}`}
          component='svg'
          viewBox='0 0 20 40'
          aria-hidden
          style={
            {
              '--dx': `${feather.dx}px`,
              '--dy': `${feather.dy}px`,
              '--rotate': `${feather.rotate}deg`,
              animationDelay: `${feather.delay}ms`,
            } as CSSProperties
          }
          sx={sxStyles.feather}
        >
          <path d='M10 2 C18 10 17 26 10 36 C3 26 2 10 10 2Z' fill={feather.color} />
          <path d='M10 4 L10 39' stroke='#5a3418' strokeWidth='1.3' />
        </Box>
      ))}
      <Box
        component='button'
        type='button'
        aria-label='Turkey'
        onClick={handleGobble}
        sx={[sxStyles.turkeyButton, { transform: `scaleX(${direction})` }]}
      >
        {/* viewBox has room on the left and top for the fanned-out tail */}
        <svg viewBox='-14 -10 76 68' width={TURKEY_WIDTH} height={TURKEY_HEIGHT} overflow='visible' aria-hidden>
          <g css={{ animation: `${bob} 0.36s ease-in-out infinite alternate`, transformOrigin: '30px 50px' }}>
            {TAIL_ANGLES.map((angle, i) => (
              <ellipse
                key={angle}
                cx='22'
                cy='12'
                rx='6'
                ry='15'
                transform={`rotate(${angle} 22 27)`}
                fill={FEATHER_COLORS[i % FEATHER_COLORS.length]}
              />
            ))}
            <circle cx='28' cy='32' r='13' fill='#6b3f22' />
            <path d='M36 30 q6 -4 6 -10' stroke='#7a4a2a' strokeWidth='5' fill='none' strokeLinecap='round' />
            <circle cx='43' cy='19' r='5.5' fill='#8a5a3a' />
            <polygon points='47.5,18 53,20 47.5,22' fill='#e8b04a' />
            <circle cx='44.5' cy='17.5' r='1' fill='#111' />
            <ellipse cx='47.5' cy='24' rx='1.5' ry='3' fill='#c0392b' />
          </g>
          {[
            { x1: 25, x2: 24, reverse: false },
            { x1: 31, x2: 32, reverse: true },
          ].map((leg) => (
            <line
              key={leg.x1}
              x1={leg.x1}
              y1='44'
              x2={leg.x2}
              y2='54'
              stroke='#e0a43a'
              strokeWidth='2.2'
              strokeLinecap='round'
              css={{
                transformBox: 'fill-box',
                transformOrigin: 'top center',
                animation: `${step} 0.36s ease-in-out infinite ${leg.reverse ? 'alternate-reverse' : 'alternate'}`,
                animationPlayState: isGobbling ? 'paused' : 'running',
              }}
            />
          ))}
        </svg>
      </Box>
    </Box>
  );
};

const sxStyles = {
  container: {
    position: 'fixed',
    left: 0,
    bottom: 0,
    width: TURKEY_WIDTH,
    pointerEvents: 'none',
    zIndex: 1250,
  },
  turkeyButton: {
    display: 'block',
    padding: 0,
    border: 0,
    background: 'none',
    cursor: 'pointer',
    pointerEvents: 'auto',
  },
  bubble: {
    position: 'absolute',
    bottom: '100%',
    left: 10,
    padding: '3px 8px',
    borderRadius: '10px',
    background: '#fff',
    color: '#5a2e12',
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
    animation: `${gobblePop} 0.9s ease-out forwards`,
  },
  feather: {
    position: 'absolute',
    left: TURKEY_WIDTH / 2 - 5,
    top: TURKEY_HEIGHT / 3,
    width: 10,
    height: 20,
    opacity: 0,
    pointerEvents: 'none',
    animation: `${featherFly} ${FEATHER_BURST_LENGTH}ms cubic-bezier(0.2, 0.7, 0.3, 1) forwards`,
  },
};
